import React, { useState, useEffect } from 'react';
import { QcCheck } from '../models/qc-check';
import { BenchSheetTechnologyType, Entities } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Paging,
    Button as ColumnButton,
    Toolbar,
    Selection,
    Lookup,
    Editing,
    Item as ToolbarItem
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';

const riQcCheckTypes = [
    { id: 1, name: "Water" },
    { id: 2, name: "Laser Liquid" }
];

const orQcCheckTypes = [
    { id: 1, name: "Quartz" },
    { id: 2, name: "NIST Sucrose" }
];

export interface Props {
    benchSheetId?: string;
    spotCheckBenchSheetId?: string;
    technologyId: BenchSheetTechnologyType;
    isReadOnly: boolean;
    datagridHeight?: string;
    title?: string;
    headerBgClass?: string;
}

export default function QCCheckComponent(props: Props) {
    const [qcChecks, setQcChecks] =
        useState<QcCheck[]>();

    const [selectedQcCheck, setselectedQcCheck] =
        useState<QcCheck>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId]);

    const enumIdColumnLabel = () => {
        if (props.technologyId === BenchSheetTechnologyType.RefractiveIndex)
            return "Fluid Type";
        else if (props.technologyId === BenchSheetTechnologyType.OpticalRotation)
            return "Material Type";
    }

    const enumIdDataSource = () => {
        if (props.technologyId === BenchSheetTechnologyType.RefractiveIndex)
            return riQcCheckTypes ? riQcCheckTypes : [];
        else if (props.technologyId === BenchSheetTechnologyType.OpticalRotation)
            return orQcCheckTypes ? orQcCheckTypes : [];
    }

    const sampleNameLabel = () => {
        if (props.technologyId === BenchSheetTechnologyType.OpticalRotation)
            return "Type";
        else
            return "Sample Name";
    }

    const displayEnumField = (props.technologyId === BenchSheetTechnologyType.RefractiveIndex || props.technologyId === BenchSheetTechnologyType.OpticalRotation);
    const displayCertifiedValueField = (props.technologyId === BenchSheetTechnologyType.Conductivity || props.technologyId === BenchSheetTechnologyType.UvVisSpectroscopy);
    const displayAssetNumberField = (props.technologyId === BenchSheetTechnologyType.UvVisSpectroscopy || props.technologyId === BenchSheetTechnologyType.OpticalRotation);

    return (
        <div>
            <div className="pw-comp-row">
                <DataGrid id="QcCheckDataGrid"
                    height={props.datagridHeight ? props.datagridHeight : "10rem"}
                    keyExpr="qcCheckId"
                    dataSource={qcChecks ? qcChecks : []}
                    onRowValidating={onQcCheckValidating}
                    onRowRemoving={onQcCheckRemoving}
                    rowAlternationEnabled={true}
                    showBorders={true}>

                    <Paging enabled={false} />
                    <Selection mode='single' />

                    <Column type="buttons" width='8rem' >
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={showQcCheckAuditInformationDialog} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onQcCheckInformationButtonClicked} />
                    </Column>

                    {props.technologyId !== BenchSheetTechnologyType.RefractiveIndex &&
                        <Column dataField='sampleName' caption={sampleNameLabel()} dataType="string" width="8rem" />
                    }

                    {displayEnumField &&
                        <Column dataField='enumId' caption={enumIdColumnLabel()} dataType="string" width="8rem" >
                            <Lookup dataSource={enumIdDataSource()}
                                allowClearing={true}
                                displayExpr='name' valueExpr='id' />
                        </Column>
                    }

                    {props.technologyId === BenchSheetTechnologyType.Conductivity &&
                        <Column dataField='lotNumber' caption='Lot #' dataType="string" width="8rem" />
                    }

                    {displayAssetNumberField &&
                        <Column dataField='assetNumber' caption='Asset ID' dataType="string" width="8rem" />
                    }

                    {props.technologyId !== BenchSheetTechnologyType.UvVisSpectroscopy &&
                        <Column dataField='spec' dataType="string" width="12rem" />
                    }

                    {displayCertifiedValueField &&
                        <Column dataField='certifiedValue' caption='Certified Value' dataType="string" width="8rem" />
                    }

                    <Column dataField='result' dataType="number" width="8rem" />
                    <Column />
                    {/* cellRender={checkWeightCellRender} format={decimalFormat} */}

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">{props.title}</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem name="addRowButton" location='after' />
                    </Toolbar>

                    <Editing newRowPosition="last"
                        mode="row"
                        useIcons={true}
                        allowAdding={!props.isReadOnly}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={!props.isReadOnly} >
                    </Editing>
                </DataGrid>
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    async function onQcCheckValidating(e: any) {
        //todo add benchsheet to each insert
        console.log("onQcCheckValidating");
        console.log(e);

        if (!props.benchSheetId)
            return;

        if (e.oldData) {
            let qcCheck = e.oldData as QcCheck;

            qcCheck.benchSheetId = props.spotCheckBenchSheetId ? props.spotCheckBenchSheetId : props.benchSheetId;

            if (qcCheck) {
                let updatedQcCheck = { ...qcCheck, ...e.newData };

                /*
                if(validateEntries(updatedQcCheck) !== true) {
                    console.log("Problem");
                    return;
                }
                */

                await updateQcCheck(updatedQcCheck)
                    .then(() => {
                        getQcChecks();
                        //showToast("Raw material updated", "success");
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating QC Check");
                    });
            }
        }
        else {
            if (e.newData) {
                let qcCheck = e.newData as QcCheck;

                /*
                if(validateEntries(qcCheck) !== true) {
                    console.log("Problem");
                    return;
                }
                */

                console.log("validated QC Check");
                console.log(qcCheck);

                qcCheck.benchSheetId = props.spotCheckBenchSheetId ? props.spotCheckBenchSheetId : props.benchSheetId;

                if (qcCheck) {
                    await insertQcCheck(qcCheck)
                        .then(() => {
                            getQcChecks();
                            //showToast("Raw material saved", "success");

                        })
                        .catch(res => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting QC Check");
                        });
                }
            }
        }
    }

    async function onQcCheckRemoving(e: any) {
        console.log("onQcCheckRemoving");
        console.log(e);

        if (e.data && e.data.qcCheckId) {

            await deleteQcCheck(e.data.qcCheckId)
                .then(data => {
                    //showToast("Raw material saved", "success");

                })
                .catch(res => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting QC Check");
                });
        }
    }

    function checkWeightCellRender(e: any) {


    }

    async function getQcChecks() {
        const url = props.spotCheckBenchSheetId ? `${props.benchSheetId}/${props.spotCheckBenchSheetId}` : props.benchSheetId;

        await CrudFunctions.getData(`benchsheets/qcchecks/${url}`)
            .then(data => {
                console.log(data);

                setQcChecks(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function saveData(editedQcChecks: QcCheck[]) {
        await CrudFunctions.putData("benchsheets/qcchecksdata", editedQcChecks)
            .then(data => {
                getQcChecks();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function insertQcCheck(editedQcCheck: QcCheck) {
        await CrudFunctions.postData("benchsheets/qccheck", editedQcCheck)
            .then(data => {

                //getQcChecks();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Inserting QC Check");
            });
    }

    async function updateQcCheck(editedQcCheck: QcCheck) {
        await CrudFunctions.putData("benchsheets/qccheck", editedQcCheck)
            .then(data => {

                //getQcChecks();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating QC Check");
            });
    }


    async function deleteQcCheck(qcCheckId: string) {
        await CrudFunctions.deleteData(`benchsheets/qccheck/${qcCheckId}`)
            .then(data => {

                //getQcChecks();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Deleting QC Check");
            });
    }

    async function getAllData() {
        await getQcChecks();
        //todo getEnum values

    }

    /*
    function validateEntries(qcCheck: QcCheck) {
        if (props.technologyId === BenchSheetTechnologyType.OpticalRotation) {

            if (qcCheck.sampleName) {

                console.log("IS sample name:");
                console.log(qcCheck.sampleName);

                const fullBeginIdx = qcCheck.sampleName.search(/before/i);

                if (fullBeginIdx === 0) {
                    console.log(306);
                    qcCheck.sampleName = "Before";
                    return true;
                }
                const bIdx = qcCheck.sampleName.search(/^[Bb]/);

                if (bIdx > -1) {
                    console.log(313);
                    qcCheck.sampleName = "Before";
                    return true;
                }

                const fullAfterIdx = qcCheck.sampleName.search(/after/i);

                if (fullBeginIdx === 0) {
                    console.log(321);
                    qcCheck.sampleName = "After";
                    return true;
                }
                const aIdx = qcCheck.sampleName.search(/^[Aa]/);

                if (aIdx > -1) {
                    console.log(328);
                    qcCheck.sampleName = "After";
                    return true;
                }
            }

            console.log("returning false (334)...");
            return false;
        }

        console.log("returning true (338)...");
        return true;
    }
    */

    function showQcCheckAuditInformationDialog(e: any) {

    }

    function onQcCheckInformationButtonClicked(e: any) {


    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}

